import { VisitType } from 'src/requester';
import { PageView } from './pageView';
import { VisitorDataFiltersType } from 'src/utilities/types';
import {
  KameleoonData,
  KameleoonDataType,
  SingleDataType,
  MultipleDataType,
  VisitProcessorDataType,
  KCSType,
  VisitorProcessorParametersType,
  CBSType,
} from './types';
import { CustomData } from './customData';
import { Conversion } from './conversion';
import { Browser } from './browser';
import { Device } from './device';
import { OperatingSystem } from './operatingSystem';
import { GeolocationData } from './geolocation';
import { FeatureFlagVariationsType } from 'src/storage';
import { VisitsData } from './visitsData';
import { DataManager } from './dataManager';
import { KameleoonConversionScore } from './kameleoonConversionScore';
import { TrackingStatus } from 'src/types';
import { MappingIdentifierType } from 'src/storage/types';
import { Personalization } from './personalization';
import { CBScores } from 'src/kameleoonData/cbScores';

interface IVisitProcessor {
  processVisit(visit: VisitType): void;
  processKcs(kcsData?: KCSType): void;
  processCbs(cbsData?: CBSType): void;
  processMultipleDataTypes(): void;
  processMappingIdentifier(
    visit?: VisitType,
  ): MappingIdentifierType | undefined;
  processVisitsData(visit: VisitType): void;
}

export class VisitProcessor implements IVisitProcessor {
  private visitsData = new VisitsData([]);
  private visitorData: KameleoonDataType[] = [];
  private storageVisitorData: KameleoonDataType[] = [];
  private experimentData: FeatureFlagVariationsType = {};
  private filters: VisitorDataFiltersType;
  private visitorDataTypes: Set<KameleoonData>;
  private dataManager: DataManager;
  private singleDataTypes: SingleDataType = {
    [KameleoonData.Device]: null,
    [KameleoonData.Browser]: null,
    [KameleoonData.GeolocationData]: null,
    [KameleoonData.OperatingSystem]: null,
    [KameleoonData.KameleoonConversionScore]: null,
    [KameleoonData.CBScores]: null,
  };
  private multipleDataTypes: MultipleDataType = {
    [KameleoonData.CustomData]: new Map<number, CustomData>(),
    [KameleoonData.PageView]: new Map<string, PageView>(),
    [KameleoonData.Conversion]: new Map<number, Conversion>(),
    [KameleoonData.Personalization]: new Map<number, Personalization>(),
  };

  constructor({
    filters,
    dataManager,
    visitorCode,
  }: VisitorProcessorParametersType) {
    this.filters = filters;
    this.dataManager = dataManager;
    this.visitorDataTypes = dataManager.getVisitorStoredDataTypes(visitorCode);
  }

  get data(): VisitProcessorDataType {
    return {
      visitorData: this.visitorData,
      storageVisitorData: this.storageVisitorData,
      visitsData: this.visitsData,
      experimentData: this.experimentData,
    };
  }

  public processVisit(visit: VisitType): void {
    this.processBrowser(visit);
    this.processDevice(visit);
    this.processOperatingSystem(visit);
    this.processGeolocationData(visit);
    this.processCustomData(visit);
    this.processConversion(visit);
    this.processPageView(visit);
    this.processExperiments(visit);
    this.processPersonalization(visit);
  }

  public processMultipleDataTypes(): void {
    for (const [dataType, data] of Object.entries(this.multipleDataTypes)) {
      if (!data.size) {
        continue;
      }

      switch (dataType) {
        case KameleoonData.PageView: {
          this.visitorData.push(...data.values());
          this.storageVisitorData.push(...data.values());

          break;
        }
        case KameleoonData.Personalization:
        case KameleoonData.Conversion: {
          this.storageVisitorData.push(...data.values());

          break;
        }
        case KameleoonData.CustomData: {
          const dataItem = data as Map<number, CustomData>;

          dataItem.forEach((customData) => {
            this.visitorData.push(customData);

            if (
              !this.dataManager.storedCustomDataIndexes.has(
                customData.data.index,
              )
            ) {
              this.storageVisitorData.push(customData);
            }
          });

          break;
        }
      }
    }
  }

  public processKcs(kcsData?: KCSType): void {
    if (this.filters.kcs && kcsData) {
      const kcs = new KameleoonConversionScore(kcsData);

      this.storageVisitorData.push(kcs);
    }
  }

  public processVisitsData(visit: VisitType): void {
    this.visitsData.timestamp = {
      timeStarted: visit.timeStarted,
      timeLastActivity: visit.timeLastEvent ?? visit.timeStarted,
    };
  }

  public processVisitNumber(visit: VisitType, isCurrentVisit: boolean): void {
    if (visit.staticDataEvent) {
      const visitNumber =
        visit.staticDataEvent.data.visitNumber + (isCurrentVisit ? 0 : 1);
      this.visitsData.updateVisitNumber(visitNumber);
    }
  }

  public processCbs(cbsData?: CBSType): void {
    if (this.filters.cbs && cbsData) {
      const cbs = new CBScores(cbsData);

      this.storageVisitorData.push(cbs);
    }
  }

  private processBrowser(visit: VisitType): void {
    if (this.filters.browser && !this.singleDataTypes[KameleoonData.Browser]) {
      const browser = Browser._fromVisit(visit);

      if (browser) {
        if (!this.visitorDataTypes.has(KameleoonData.Browser)) {
          this.storageVisitorData.push(browser);
        }
        this.visitorData.push(browser);
        this.singleDataTypes[KameleoonData.Browser] = browser;
      }
    }
  }

  private processDevice(visit: VisitType): void {
    if (this.filters.device && !this.singleDataTypes[KameleoonData.Device]) {
      const device = Device._fromVisit(visit);

      if (device) {
        if (!this.visitorDataTypes.has(KameleoonData.Device)) {
          this.storageVisitorData.push(device);
        }

        this.visitorData.push(device);
        this.singleDataTypes[KameleoonData.Device] = device;
      }
    }
  }

  private processOperatingSystem(visit: VisitType): void {
    if (
      this.filters.operatingSystem &&
      !this.singleDataTypes[KameleoonData.OperatingSystem]
    ) {
      const operatingSystem = OperatingSystem._fromVisit(visit);

      if (operatingSystem) {
        if (!this.visitorDataTypes.has(KameleoonData.OperatingSystem)) {
          this.storageVisitorData.push(operatingSystem);
        }

        this.visitorData.push(operatingSystem);
        this.singleDataTypes[KameleoonData.OperatingSystem] = operatingSystem;
      }
    }
  }

  private processGeolocationData(visit: VisitType): void {
    if (
      this.filters.geolocation &&
      !this.singleDataTypes[KameleoonData.GeolocationData]
    ) {
      const geolocation = GeolocationData._fromVisit(visit);

      if (geolocation) {
        if (!this.visitorDataTypes.has(KameleoonData.GeolocationData)) {
          this.storageVisitorData.push(geolocation);
        }

        this.visitorData.push(geolocation);
        this.singleDataTypes[KameleoonData.GeolocationData] = geolocation;
      }
    }
  }

  private processCustomData(visit: VisitType): void {
    if (this.filters.customData) {
      CustomData._updateFromVisit(
        visit,
        this.multipleDataTypes[KameleoonData.CustomData],
      );
    }
  }

  private processConversion(visit: VisitType): void {
    if (this.filters.conversions) {
      // --- Note ---
      // We collect Conversion data with only unique goalId
      Conversion._updateFromVisit(
        visit,
        this.multipleDataTypes[KameleoonData.Conversion],
      );

      // --- Note ---
      // We collect all Conversion data with goalId, revenue and negative for all visits
      const conversions = Conversion._listFromVisit(visit);

      if (conversions.length) {
        this.visitorData.push(...conversions);
      }
    }
  }

  private processPageView(visit: VisitType): void {
    if (this.filters.pageViews) {
      PageView._updateFromVisit(
        visit,
        this.multipleDataTypes[KameleoonData.PageView],
      );
    }
  }

  private processExperiments(visit: VisitType): void {
    if (this.filters.experiments && visit.experimentEvents) {
      for (const experimentEvent of visit.experimentEvents) {
        const { id, variationId } = experimentEvent.data;

        this.experimentData[id] = {
          variationId,
          isTargetedRule: false,
          status: TrackingStatus.Unsent,
        };
      }
    }
  }

  private processPersonalization(visit: VisitType): void {
    if (this.filters.personalization) {
      Personalization._updateFromVisit(
        visit,
        this.multipleDataTypes[KameleoonData.Personalization],
      );
    }
  }

  public processMappingIdentifier(
    visit?: VisitType,
  ): MappingIdentifierType | undefined {
    if (!visit) {
      return;
    }
    const timestamp = visit.customDataEvents?.find(
      (event) => event.data.mappingIdentifier,
    )?.time;
    if (timestamp && visit.visitorCode) {
      return {
        mappingIdentifier: visit.visitorCode,
        timestamp,
      };
    }
  }
}
